import { of, throwError, catchError, map, switchMap } from 'rxjs';
import type { Observable } from 'rxjs';
import {
  innerV2IdentitiesDisplayNameAvailabilityCreate,
  innerV2IdentitiesEmailAvailabilityCreate,
} from 'services/api/proxy-registration/inner';
import { mePerformersList } from 'services/api/account-services/me';
import { ServiceBase } from 'services';
import account from 'store/account';
import is from 'utils/is';

interface DisplayNameAvailability {
  isAvailable: boolean;
  bannedWord?: string | undefined;
}

enum EmailAvailabilityStatus {
  Available = 'available',
  Unavailable = 'unavailable',
  Invalid = 'invalid',
}

interface EmailAvailability {
  status: EmailAvailabilityStatus;
  email: string;
}

class Registration extends ServiceBase {
  name = 'registration';

  getPerformerId$(): Observable<number> {
    return this.call$(
      mePerformersList(
        { 'filter[statuses]': ['new', 'pending', 'rejected', 'active', 'inactive'] },
        {
          headers: {
            'X-Actor-Type': 'user',
            'X-Actor-Id': account.data?.userId ?? '',
          },
        }
      )
    ).pipe(
      map((response) => response?.data?.data),
      map((perfList) => perfList?.[0]?.id),
      switchMap((id) => {
        if (is.nullish(id)) {
          return throwError(() => new Error('[performer list]: Response was successful but did not return any items.'));
        }

        return of(id);
      })
    );
  }

  checkDisplayNameAvailability$(displayName: string, categoryId?: number): Observable<DisplayNameAvailability> {
    return this.call$(
      innerV2IdentitiesDisplayNameAvailabilityCreate(
        { displayName, categoryId },
        {
          headers: {
            'X-Actor-Type': 'user',
            'X-Actor-Id': account.data?.userId ?? '',
          },
        }
      )
    ).pipe(
      map((response) => ({
        isAvailable: response.data.data?.isAvailable ?? true,
        bannedWord: response.data.data?.bannedWord ?? undefined,
      })),
      catchError(() =>
        of<DisplayNameAvailability>({
          isAvailable: true,
          bannedWord: undefined,
        })
      )
    );
  }

  checkEmailAvailability$(email: string): Observable<EmailAvailability> {
    return this.call$(innerV2IdentitiesEmailAvailabilityCreate({ email })).pipe(
      map((response): EmailAvailability => {
        if (response.data.data?.isValid === false) return { status: EmailAvailabilityStatus.Invalid, email };
        if (response.data.data?.isAvailable === false) return { status: EmailAvailabilityStatus.Unavailable, email };

        return { status: EmailAvailabilityStatus.Available, email };
      }),
      /**
       * expected result is: once throttle is reached, which produces HTTP error we should
       * capture the error and provide a positive feedback so the button is not blocked;
       * validation is then done on form data POST
       */
      catchError(() => of({ status: EmailAvailabilityStatus.Available, email } as EmailAvailability))
    );
  }
}

export { EmailAvailabilityStatus };
export type { DisplayNameAvailability, EmailAvailability };
export default new Registration();
